import React, { Component } from 'react'
import { toast } from 'react-toastify'
import { getString } from '../../../locale'
import {
    activateLicense,
    checkLicenseNow,
    getLicensePolicy,
    getLicenseStatus,
    LicensePolicyResponse,
    LicenseStatusResponse,
    setLicensePolicy,
    tempUnlockLicense,
} from '../../../api/0x0005/general-settings'

type Props = {
    clientId: string
}

type State = {
    loading: boolean
    busy: boolean
    status?: LicenseStatusResponse
    policy?: LicensePolicyResponse
    licenseKey: string
    requestedHours: number
}

const STATUS_LABELS: Record<number, string> = {
    0: 'license.status_not_required',
    1: 'license.status_waiting_ntp',
    2: 'license.status_trial',
    3: 'license.status_active',
    4: 'license.status_temp_unlock',
    5: 'license.status_expired',
    6: 'license.status_revoked',
    7: 'license.status_none',
}

export default class LicenseTab extends Component<Props, State> {
    state: State = {
        loading: true,
        busy: false,
        licenseKey: '',
        requestedHours: 0,
    }

    async componentDidMount() {
        await this.load()
    }

    load = async () => {
        try {
            this.setState({ loading: true })
            const [status, policy] = await Promise.all([
                getLicenseStatus(this.props.clientId),
                getLicensePolicy(this.props.clientId),
            ])
            this.setState({
                status,
                policy,
                requestedHours: Math.round((policy.defaultTempUnlockSec || 0) / 3600),
            })
        } catch (error: any) {
            toast.error(error?.message || getString('license.load_error'))
        } finally {
            this.setState({ loading: false })
        }
    }

    setPolicy = async () => {
        const { policy } = this.state
        if (!policy) return
        try {
            this.setState({ busy: true })
            const response = await setLicensePolicy(
                this.props.clientId,
                policy.required ? 1 : 0,
                policy.trialDays,
                policy.defaultTempUnlockSec,
            )
            if (response.success === 1) {
                toast.success(response.message || getString('license.policy_updated'))
                await this.load()
            } else {
                toast.error(response.message || getString('license.policy_update_failed'))
            }
        } catch (error: any) {
            toast.error(error?.message || getString('license.policy_update_failed'))
        } finally {
            this.setState({ busy: false })
        }
    }

    activate = async () => {
        const licenseKey = this.state.licenseKey.trim()
        if (!licenseKey) {
            toast.error(getString('license.license_key_required'))
            return
        }
        try {
            this.setState({ busy: true })
            const response = await activateLicense(this.props.clientId, licenseKey)
            if (response.success === 1) {
                toast.success(response.message || getString('license.activated'))
                this.setState({ licenseKey: '' })
                await this.load()
            } else {
                toast.error(response.message || getString('license.activation_failed'))
            }
        } catch (error: any) {
            toast.error(error?.message || getString('license.activation_failed'))
        } finally {
            this.setState({ busy: false })
        }
    }

    checkNow = async () => {
        try {
            this.setState({ busy: true })
            const response = await checkLicenseNow(this.props.clientId)
            if (response.success === 1) {
                toast.success(response.message || getString('license.check_complete'))
                await this.load()
            } else {
                toast.error(response.message || getString('license.check_failed'))
            }
        } catch (error: any) {
            toast.error(error?.message || getString('license.check_failed'))
        } finally {
            this.setState({ busy: false })
        }
    }

    tempUnlock = async () => {
        try {
            this.setState({ busy: true })
            const requestedSeconds = Math.max(0, Math.round(this.state.requestedHours * 3600))
            const response = await tempUnlockLicense(this.props.clientId, requestedSeconds)
            if (response.success === 1) {
                toast.success(response.message || getString('license.temp_unlock_granted'))
                await this.load()
            } else {
                toast.error(response.message || getString('license.temp_unlock_failed'))
            }
        } catch (error: any) {
            toast.error(error?.message || getString('license.temp_unlock_failed'))
        } finally {
            this.setState({ busy: false })
        }
    }

    formatEpoch = (seconds: number): string => {
        if (!seconds) return '-'
        const date = new Date(seconds * 1000)
        return Number.isNaN(date.getTime()) ? '-' : date.toLocaleString()
    }

    render() {
        const { loading, busy, status, policy } = this.state
        if (loading) {
            return (
                <div className="flex items-center justify-center h-64">
                    <div className="text-lg text-gray-600 dark:text-gray-400">{getString('common.loading')}</div>
                </div>
            )
        }

        return (
            <div className="p-6 space-y-6 text-gray-800 dark:text-gray-100">
                <h1 className="text-2xl font-bold text-gray-800 dark:text-white">{getString('license.title')}</h1>
                <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-6 space-y-3 text-sm text-gray-800 dark:text-gray-100">
                    <div>{getString('license.required')}: {status?.required === 1 ? getString('license.yes') : getString('license.no')}</div>
                    <div>{getString('license.status')}: {getString((STATUS_LABELS[status?.statusCode ?? 7] as any) || 'license.status_none')}</div>
                    <div>{getString('license.can_start_new_pour')}: {status?.canStartNewPour === 1 ? getString('license.yes') : getString('license.no')}</div>
                    <div>{getString('license.ntp_synced_once')}: {status?.ntpSyncedOnce === 1 ? getString('license.yes') : getString('license.no')}</div>
                    <div>{getString('license.temp_unlock_used_cycle')}: {status?.tempUnlockUsedForCycle === 1 ? getString('license.yes') : getString('license.no')}</div>
                    <div>{getString('license.plan')}: {status?.plan || '-'}</div>
                    <div>{getString('license.cycle_id')}: {status?.cycleId ?? '-'}</div>
                    <div>{getString('license.activation')}: {this.formatEpoch(status?.activationEpochSec ?? 0)}</div>
                    <div>{getString('license.expiry')}: {this.formatEpoch(status?.expiresEpochSec ?? 0)}</div>
                    <div>{getString('license.temp_unlock_until')}: {this.formatEpoch(status?.tempUnlockUntilEpochSec ?? 0)}</div>
                    <div>{getString('license.last_check')}: {this.formatEpoch(status?.lastCheckEpochSec ?? 0)}</div>
                    <div>{getString('license.seconds_until_expiry')}: {status?.secondsUntilExpiry ?? 0}</div>
                    <div>{getString('license.seconds_until_temp_unlock_end')}: {status?.secondsUntilTempUnlockEnd ?? 0}</div>
                    {status?.message && <div>{getString('license.message')}: {status.message}</div>}
                    <div className="pt-2">
                        <button
                            onClick={this.load}
                            disabled={busy}
                            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded disabled:opacity-70"
                        >
                            {getString('license.refresh')}
                        </button>
                    </div>
                </div>

                <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-6 space-y-3 text-gray-800 dark:text-gray-100">
                    <h2 className="text-lg font-semibold text-gray-800 dark:text-white">{getString('license.activation_section')}</h2>
                    <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300">{getString('license.license_key')}</label>
                    <input
                        type="text"
                        value={this.state.licenseKey}
                        onChange={e => this.setState({ licenseKey: e.target.value })}
                        placeholder={getString('license.license_key')}
                        className="w-full rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-gray-800 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                    />
                    <div className="flex gap-2">
                        <button
                            onClick={this.activate}
                            disabled={busy}
                            className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded disabled:opacity-70"
                        >
                            {getString('license.activate')}
                        </button>
                        <button
                            onClick={this.checkNow}
                            disabled={busy}
                            className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded disabled:opacity-70"
                        >
                            {getString('license.check_now')}
                        </button>
                    </div>
                </div>

                <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-6 space-y-3 text-gray-800 dark:text-gray-100">
                    <h2 className="text-lg font-semibold text-gray-800 dark:text-white">{getString('license.temp_unlock_section')}</h2>
                    <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300">{getString('license.requested_seconds')}</label>
                    <input
                        type="number"
                        min={0}
                        max={720}
                        value={this.state.requestedHours}
                        onChange={e => this.setState({ requestedHours: Number.parseInt(e.target.value, 10) || 0 })}
                        className="w-full rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-gray-800 dark:text-white"
                    />
                    <button
                        onClick={this.tempUnlock}
                        disabled={busy}
                        className="px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded disabled:opacity-70"
                        style={{ backgroundColor: '#ea580c', color: '#ffffff' }}
                    >
                        {getString('license.request_temp_unlock')}
                    </button>
                </div>

                <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-6 space-y-3 text-gray-800 dark:text-gray-100">
                    <h2 className="text-lg font-semibold text-gray-800 dark:text-white">{getString('license.policy_section')}</h2>
                    <label className="flex items-center gap-3 text-gray-700 dark:text-gray-300">
                        <input
                            type="checkbox"
                            checked={(policy?.required ?? 0) === 1}
                            onChange={e =>
                                this.setState({
                                    policy: {
                                        required: e.target.checked ? 1 : 0,
                                        trialDays: policy?.trialDays ?? 0,
                                        defaultTempUnlockSec: policy?.defaultTempUnlockSec ?? 86400,
                                    },
                                })
                            }
                        />
                        {getString('license.license_required')}
                    </label>
                    <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300">{getString('license.trial_days')}</label>
                    <input
                        type="number"
                        min={0}
                        max={3650}
                        value={policy?.trialDays ?? 0}
                        onChange={e =>
                            this.setState({
                                policy: {
                                    required: policy?.required ?? 0,
                                    trialDays: Number.parseInt(e.target.value, 10) || 0,
                                    defaultTempUnlockSec: policy?.defaultTempUnlockSec ?? 86400,
                                },
                            })
                        }
                        className="w-full rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-gray-800 dark:text-white"
                    />
                    <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300">{getString('license.default_temp_unlock_sec')}</label>
                    <input
                        type="number"
                        min={1}
                        max={720}
                        value={Math.round((policy?.defaultTempUnlockSec ?? 86400) / 3600)}
                        onChange={e =>
                            this.setState({
                                policy: {
                                    required: policy?.required ?? 0,
                                    trialDays: policy?.trialDays ?? 0,
                                    defaultTempUnlockSec: (Number.parseInt(e.target.value, 10) || 1) * 3600,
                                },
                            })
                        }
                        className="w-full rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-gray-800 dark:text-white"
                    />
                    <button
                        onClick={this.setPolicy}
                        disabled={busy}
                        className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded disabled:opacity-70"
                    >
                        {getString('common.save')}
                    </button>
                </div>
            </div>
        )
    }
}
